import { useState } from 'react';
import { Button } from '../../ui/primitives/Button.tsx';
import { Surface } from '../../ui/primitives/Surface.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './profile.module.css';

/**
 * The invite — worth more than a user search at this size.
 *
 * IT MATCHES HOW CAVAQUINHO STUDENTS ACTUALLY ARRIVE: someone's teacher,
 * someone's roda, someone's group chat. A search box assumes the learner knows
 * a name to type and that the platform has enough people for typing it to find
 * anyone; a link assumes somebody already knows somebody, which is the true
 * thing at forty accounts.
 *
 * IT IS NOT A TOKEN, AND THERE IS NO INVITE TABLE. The link is the learner's
 * own profile address. There is nothing to revoke because it grants nothing —
 * opening it shows a public profile to a signed-in learner and offers a follow,
 * which is exactly what that address does when it is typed rather than shared.
 * A token would be a second name for the same page, plus storage, plus an
 * expiry nobody asked for.
 *
 * A VISITOR WITHOUT AN ACCOUNT MEETS SIGN-IN FIRST, and is returned here
 * afterwards — the route boundary already does that for every address, so an
 * invite sent to somebody who has not subscribed yet behaves like every other
 * link into the product.
 */
export function InviteLink({ userId }: { userId: string }) {
  const url = `${window.location.origin}/convite/${userId}`;
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // A denied clipboard is not a failure worth a message: the address is
      // on screen and selectable, which is how it was shared before there
      // were buttons for it.
      setCopied(false);
    }
  }

  return (
    <Surface layer={1} radius="2xl" inset as="section">
      <div className={styles.invite}>
        <Text as="h2" variant="subheading">
          Seu convite
        </Text>
        <Text as="p" variant="body-sm" tone="muted">
          Mande para quem toca com você. Um ranking entre cinco pessoas que se conhecem vale mais
          do que um entre quarenta estranhos.
        </Text>

        <div className={styles.inviteRow}>
          {/* Selectable, so the link works even where the clipboard does not. */}
          <code className={styles.inviteUrl}>{url}</code>
          <Button variant="secondary" size="compact" onClick={() => void copy()}>
            {copied ? 'Copiado' : 'Copiar'}
          </Button>
        </div>
      </div>
    </Surface>
  );
}
